/**
 * BookingEngineSection — BookingEngine/Direct/HomeAway Owner Payout label and tooltip copy for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 3c extracted from BookingDetailModal.jsx; JSX and strings unchanged.
 *
 * Purpose:
 *   Render BookingEngine/Direct/HomeAway folio label content ("Paid to Owner" with optional payoutLabel
 *   parenthetical) and provide tooltip copy for the Direct Booking (Website) payout info icon.
 *   Presentational only; parent passes theme, hostPayout; parent owns predicate flags and tooltip show/hide.
 *
 * Assumptions & Invariants:
 *   - Presentational; no fetch; no admin imports.
 *   - getBookingEnginePayoutTooltipText is pure: same inputs → same string. Call only when at least one
 *     of isBookingEngine, isDirect, isHomeAway is true.
 *
 * Dependencies: React only
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 3c extraction from BookingDetailModal (BookingEngine/Direct/HomeAway label + tooltip helper).
 */

const labelSubtextStyle = {
  fontSize: '12px',
  fontStyle: 'italic',
  opacity: 0.8,
  marginLeft: '4px',
};

/** Crissy: CLC Direct Booking (Website) / bookingengine only. Single sentence. */
const BOOKINGENGINE_PAYOUT_TOOLTIP = 'Direct Booking (Website): Payout reflects the net amount deposited after Stripe processing fees.';

/** Legacy tooltip for direct/homeaway (non-bookingengine); preserved unchanged. */
const DIRECT_HOMEAWAY_PAYOUT_TOOLTIP = 'Direct Booking (Website): Payout reflects the net amount deposited after Stripe processing fees. Guest-paid taxes/fees are collected separately and are not deducted from owner earnings.';

/**
 * Returns BookingEngine/Direct/HomeAway tooltip string for Owner Payout info icon.
 * bookingengine only: Crissy copy (single sentence). direct/homeaway: legacy copy unchanged.
 * @param {boolean} _isFinalCopy — reserved for API parity; not used (string is identical).
 * @param {boolean} isBookingEngine
 * @param {boolean} isDirect
 * @param {boolean} isHomeAway
 * @returns {string | null}
 */
export function getBookingEnginePayoutTooltipText(_isFinalCopy, isBookingEngine, isDirect, isHomeAway) {
  if (isBookingEngine) return BOOKINGENGINE_PAYOUT_TOOLTIP;
  if (isDirect || isHomeAway) return DIRECT_HOMEAWAY_PAYOUT_TOOLTIP;
  return null;
}

/**
 * Renders BookingEngine/Direct/HomeAway Owner Payout label content ("Paid to Owner" + optional payoutLabel).
 * @param {{ theme?: string, hostPayout?: { payoutLabel?: string } }}
 */
export default function BookingEngineSection({ theme, hostPayout }) {
  if (hostPayout?.payoutLabel) {
    return (
      <>
        Paid to Owner
        <span style={labelSubtextStyle}>
          ({hostPayout.payoutLabel})
        </span>
      </>
    );
  }
  return 'Paid to Owner';
}
